<template>
    <RegularModal ref="modal" :modal-card-class="selectingCategory ? 'is-large' : undefined">
        <template v-slot:title> {{ translate("page.edit_competitor.title") }}</template>
        <template v-slot:body>
            <div class="mb-3 is-size-5" v-if="registeredCompetitor && selectingCategory">
                <span class="has-text-grey-light mr-2"> {{ translate("page.start_fights.competitor") }}: </span>
                <Country :country-code="registeredCompetitor.nationality" :only-flag="true" />
                <span class="clarity-mask">{{ registeredCompetitor.firstName }} {{ registeredCompetitor.lastName }}</span>
            </div>
            <div class="columns" v-if="registeredCompetitor">
                <div class="column" v-if="!selectingCategory">
                    <div class="mb-3 is-size-5">
                        <span class="has-text-grey-light mr-2"> {{ translate("page.start_fights.competitor") }}: </span>
                        <span>
                            <Country :country-code="registeredCompetitor.nationality" :only-flag="true" />
                            <span class="clarity-mask">{{ registeredCompetitor.firstName }} {{ registeredCompetitor.lastName }}</span>
                            <span class="has-text-grey is-size-6">#{{ registeredCompetitor.id }}</span>
                        </span>
                    </div>

                    <div class="field">
                        <label class="label">{{ translate("common.category") }}:</label>
                        <div class="control">
                            <input
                                type="text"
                                class="input has-pointer-cursor"
                                readonly
                                v-model="registeredCategory"
                                @click="switchToChangingCategory"
                            />
                        </div>
                    </div>

                    <div class="field">
                        <label class="label"> {{ translate("common.academy") }}: </label>
                        <div class="control">
                            <input
                                class="input"
                                type="text"
                                readonly
                                :value="getAcademyNameForCompetitor()"
                                @focus="openSelectAcademyModal"
                            />
                        </div>
                    </div>

                    <div class="field">
                        <label class="label"> {{ translate("common.branch_city") }}: </label>
                        <div class="control">
                            <input class="input" type="text" v-model="branch" />
                        </div>
                    </div>
                </div>
                <template v-else>
                    <div class="column">
                        <CategorySelector
                            ref="categorySelector"
                            @selected="onCategorySelected"
                            @selection-cleared="pendingCategory = null"
                            :event-id="eventId"
                            :current-parameter-id="registeredCompetitor.parameterId"
                        >
                            <template #aside>
                                <CategoryRosterPreview :event-id="eventId" :parameter-id="pendingCategory?.key ?? null" />
                            </template>
                        </CategorySelector>
                        <div class="buttons mt-2">
                            <button class="button" @click="cancelSelectingCategory">
                                <span class="icon">
                                    <i class="fa fa-undo" />
                                </span>
                                <span>{{ translate("common.go_back") }}</span>
                            </button>
                            <button class="button is-primary" :disabled="!pendingCategory" @click="chooseCategory">
                                {{ translate("bracket_category_select.select_category") }}
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button @click="close" class="button">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton :is-loading="updatingInProgress" class="button is-primary" @click="confirm" :disabled="!canSubmit">
                    {{ translate("common.save") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed, nextTick } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import CategorySelector from "./CategorySelector.vue";
    import CategoryRosterPreview from "./CategoryRosterPreview.vue";
    import Country from "../Common/Country.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { eventBus, OPEN_ACADEMY_FROM_COMPETITOR_EDIT, OPEN_SIDE_EFFECTS_REPORT_MODAL } from "./eventBus";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { successToast, warningToast } from "../Common/toasts";
    import academiesApi from "../Api/academies";
    import eventCompetitorsApi from "../Api/eventCompetitors";
    import { useRegisteredCompetitorsStore } from "./registeredCompetitorsStore";
    import { EventRegistrationSideEffectReport } from "../Common/types";
    import { CompetitorPaymentWithMetadata } from "../Api/payments";

    // Props
    const props = defineProps<{
        eventId: number;
    }>();

    // State
    const modal = ref<InstanceType<typeof RegularModal> | null>(null);
    const categorySelector = ref<InstanceType<typeof CategorySelector> | null>(null);
    const selectedCategory = ref<{ key?: number; value?: string }>({});
    const pendingCategory = ref<{ key: number; value: string } | null>(null);
    const registeredCategory = ref("");
    const branch = ref("");
    const academyId = ref(0);
    const selectingCategory = ref(false);
    const registeredCompetitor = ref<CompetitorPaymentWithMetadata | null>(null);
    const updatingInProgress = ref(false);
    const academies = ref<Array<{ key: number; value: string }>>([]);

    // Store
    const store = useRegisteredCompetitorsStore();

    // Computed
    const canSubmit = computed(() => !selectingCategory.value);

    // Methods
    function open(competitor: CompetitorPaymentWithMetadata) {
        registeredCompetitor.value = competitor;
        branch.value = competitor.branch || "";
        academyId.value = competitor.academyId || 0;
        selectingCategory.value = false;
        selectedCategory.value = {};
        pendingCategory.value = null;
        registeredCategory.value = competitor.category || "";
        modal.value?.open();

        return fetchAcademies().catch(handleError);
    }

    function confirm() {
        if (!registeredCompetitor.value) {
            return;
        }
        updatingInProgress.value = true;
        return eventCompetitorsApi
            .updateRegistration(props.eventId, registeredCompetitor.value.id, {
                parameterId: selectedCategory.value.key || registeredCompetitor.value.parameterId!,
                branch: branch.value,
                academyId: academyId.value,
            })
            .then(response => {
                modal.value?.close();

                if (response.sideEffects.length === 0) {
                    successToast(translate("flash_messages.competitor_updated"));
                } else {
                    triggerSideEffectsReportModalOpening({
                        sideEffects: response.sideEffects,
                        eventId: props.eventId,
                        //No idea why TS is reporting possible null here.
                        registeredCompetitor: registeredCompetitor.value!,
                    });
                }

                return store.getRegisteredCompetitorsPage();
            })
            .catch(handleError)
            .finally(() => {
                updatingInProgress.value = false;
            });
    }

    function onCategorySelected(selectedCat: { key: number; value: string }) {
        pendingCategory.value = selectedCat;
    }

    function chooseCategory() {
        if (!pendingCategory.value) {
            return;
        }
        selectedCategory.value = pendingCategory.value;
        registeredCategory.value = pendingCategory.value.value;
        pendingCategory.value = null;
        categorySelector.value?.close();
        selectingCategory.value = false;
    }

    function switchToChangingCategory() {
        selectingCategory.value = true;
        pendingCategory.value = null;

        nextTick(() => {
            categorySelector.value?.init();
        });
    }

    function cancelSelectingCategory() {
        selectingCategory.value = false;
    }

    function close() {
        modal.value?.close();
    }

    function getAcademyNameForCompetitor() {
        const id = academyId.value;
        if (!id) {
            return "";
        }

        const academy = academies.value.find(item => item.key === id);
        return academy?.value || "";
    }

    function fetchAcademies() {
        return academiesApi.getGlobalAcademies().then(result => {
            academies.value = result;
        });
    }

    function openSelectAcademyModal() {
        eventBus.$emit(OPEN_ACADEMY_FROM_COMPETITOR_EDIT, (item: { key: number }) => {
            academyId.value = item.key;
        });
    }

    function triggerSideEffectsReportModalOpening(report: EventRegistrationSideEffectReport) {
        if (report.sideEffects.length === 0) {
            return;
        }
        warningToast(translate("side_effects_modal.event_registration.change_caused_side_effects"), 5000);
        eventBus.$emit(OPEN_SIDE_EFFECTS_REPORT_MODAL, report satisfies EventRegistrationSideEffectReport);
    }

    // Expose methods for parent components
    defineExpose({
        open,
    });
</script>
